import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Deal, DealStage, DealFormData, DealFilters, PipelineSummary } from '@/types/crm';

interface DealStore {
  deals: Deal[];
  stages: DealStage[];
  selectedDeal: Deal | null;
  pipelineSummary: PipelineSummary[];
  isLoading: boolean;
  filters: DealFilters;
  fetchDeals: () => Promise<void>;
  fetchStages: () => Promise<void>;
  fetchDealById: (id: string) => Promise<Deal | null>;
  fetchPipelineSummary: () => Promise<void>;
  createDeal: (data: DealFormData) => Promise<Deal | null>;
  updateDeal: (id: string, data: Partial<DealFormData>) => Promise<void>;
  updateDealStage: (id: string, stage: string) => Promise<void>;
  markDealWon: (id: string) => Promise<void>;
  markDealLost: (id: string, reason: string) => Promise<void>;
  deleteDeal: (id: string) => Promise<void>;
  setSelectedDeal: (deal: Deal | null) => void;
  setFilters: (filters: DealFilters) => void;
}

export const useDealStore = create<DealStore>((set, get) => ({
  deals: [],
  stages: [],
  selectedDeal: null,
  pipelineSummary: [],
  isLoading: false,
  filters: {},

  fetchDeals: async () => {
    set({ isLoading: true });
    try {
      const { filters } = get();
      let query = supabase
        .from('crm_deals_with_details')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.stage) {
        query = query.eq('stage', filters.stage);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }
      if (filters.company_id) {
        query = query.eq('company_id', filters.company_id);
      }
      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,deal_number.ilike.%${filters.search}%,company_name.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      set({ deals: data || [] });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchStages: async () => {
    const { data, error } = await supabase
      .from('crm_deal_stages')
      .select('*')
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (error) throw error;
    set({ stages: data || [] });
  },

  fetchDealById: async (id: string) => {
    const { data, error } = await supabase
      .from('crm_deals_with_details')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) return null;
    set({ selectedDeal: data });
    return data;
  },

  fetchPipelineSummary: async () => {
    const { data, error } = await supabase.rpc('crm_get_pipeline_summary');
    if (error) throw error;
    set({ pipelineSummary: data || [] });
  },

  createDeal: async (data: DealFormData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: deal, error } = await supabase
      .from('crm_deals')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    await get().fetchDeals();
    await get().fetchPipelineSummary();
    return deal;
  },

  updateDeal: async (id: string, data: Partial<DealFormData>) => {
    const { error } = await supabase
      .from('crm_deals')
      .update(data)
      .eq('id', id);

    if (error) throw error;
    await get().fetchDeals();
    await get().fetchPipelineSummary();
  },

  updateDealStage: async (id: string, stageIdOrName: string) => {
    const { stages } = get();
    // Support both stage_id (uuid) and stage name
    const stageInfo = stages.find(s => s.id === stageIdOrName || s.name === stageIdOrName);
    
    const { error } = await supabase
      .from('crm_deals')
      .update({
        stage: stageInfo?.name || stageIdOrName,
        stage_id: stageInfo?.id,
        probability: stageInfo?.probability || 0,
        status: stageInfo?.is_won ? 'won' : stageInfo?.is_lost ? 'lost' : 'open',
        actual_close_date: stageInfo?.is_won || stageInfo?.is_lost ? new Date().toISOString().split('T')[0] : null,
      })
      .eq('id', id);

    if (error) throw error;
    await get().fetchDeals();
    await get().fetchPipelineSummary();
  },

  markDealWon: async (id: string) => {
    const { stages } = get();
    const wonStage = stages.find(s => s.is_won);
    
    const { error } = await supabase
      .from('crm_deals')
      .update({
        status: 'won',
        stage: wonStage?.name || 'Closed Won',
        stage_id: wonStage?.id,
        probability: 100,
        actual_close_date: new Date().toISOString().split('T')[0],
      })
      .eq('id', id);

    if (error) throw error;
    await get().fetchDeals();
    await get().fetchPipelineSummary();
  },

  markDealLost: async (id: string, reason: string) => {
    const { stages } = get();
    const lostStage = stages.find(s => s.is_lost);
    
    const { error } = await supabase
      .from('crm_deals')
      .update({
        status: 'lost',
        stage: lostStage?.name || 'Closed Lost',
        stage_id: lostStage?.id,
        probability: 0,
        lost_reason: reason,
        actual_close_date: new Date().toISOString().split('T')[0],
      })
      .eq('id', id);

    if (error) throw error;
    await get().fetchDeals();
    await get().fetchPipelineSummary();
  },

  deleteDeal: async (id: string) => {
    const { error } = await supabase
      .from('crm_deals')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await get().fetchDeals();
    await get().fetchPipelineSummary();
  },

  setSelectedDeal: (deal: Deal | null) => set({ selectedDeal: deal }),
  setFilters: (filters: DealFilters) => set({ filters }),
}));
